import InsertPhotoIcon from "@mui/icons-material/InsertPhoto";
import {
  ImageList,
  ImageListItem,
  Button as MuiButton,
  SxProps,
  useMediaQuery,
} from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";

import BigAddIcon from "../assets/Icons/BigAddIcon";
import NoPhotosIcon from "../assets/Icons/NoPhotosIcon";
import { NUMBER_OF_IMAGE_LIMIT } from "../constants/General";
import { imageRemoveFromDB } from "../firebase/user";
import { ImageData } from "../interfaces/General";
import { theme } from "../mui-customs/Theme";
import { RootState } from "../redux/store";
import Image from "./Image";
import NoDataUI from "./NoDataUI";
import PhotoDetailsModal from "./profile/PhotoDetailsModal";
import Typography from "./Typography";

interface ImagesGridProps {
  itemData?: ImageData[];
  readOnly?: boolean;
  onImageRemove?: (imageIndex: number) => void;
  handleUpload?: (image: File) => void;
  sx?: SxProps;
  subTitle?: string;
  maxImage?: number;
}

const ImagesGrid = ({
  itemData = [],
  readOnly = false,
  handleUpload,
  onImageRemove,
  sx,
  subTitle = "Please upload your first Photos and share to the other user",
  maxImage = NUMBER_OF_IMAGE_LIMIT,
}: ImagesGridProps) => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(NaN);
  const [imageData, setImageData] = useState(itemData);
  const userUid = useSelector((state: RootState) => state.user.user.uid);
  const userData = useSelector((state: RootState) => state.user.user);
  const isMdSize = useMediaQuery("(min-width:900px)");
  const numberOfImages = imageData.length;

  useEffect(() => {
    setImageData(itemData);
  }, [itemData.length]);

  const handlePhotoClick = (index: number) => {
    setSelectedImageIndex(index);
    setOpenModal(true);
  };

  const handleImageRemove = async () => {
    const imageRef = imageData[selectedImageIndex].imageRef;
    if (imageRef) {
      await imageRemoveFromDB(userData, imageRef);
    }
    onImageRemove && onImageRemove(selectedImageIndex);
    setOpenModal(false);
  };

  const handleChange = async (event: React.BaseSyntheticEvent) => {
    let files: File[] = Array.from(event.target.files);
    const remainingSpace = maxImage - imageData.length;
    if (files.length >= remainingSpace) {
      files = files.slice(0, remainingSpace);
      toast.error(`You can not add more than ${maxImage} images.`);
    }

    if (files.length > 0) {
      const addedImagesUrl = await Promise.all(
        Array.from(files).map(async (image: File) => {
          const url =
            event.target &&
            userUid &&
            handleUpload &&
            (await handleUpload(image));
          return url;
        })
      );

      setImageData((latestData) => [...addedImagesUrl, ...latestData]);
    }
  };
  return (
    <Box sx={sx}>
      {!(imageData.length > 0) ? (
        <Box
          sx={{
            width: "100%",
            display: "grid",
            placeItems: "center",
            mt: "36px",
          }}
        >
          {readOnly ? (
            <NoDataUI
              Icon={InsertPhotoIcon}
              displayMessage={"No images"}
              sx={{ minHeight: "200px" }}
            />
          ) : (
            <Box
              sx={{
                width: "233px",
                display: "flex",
                alignItems: "center",
                flexDirection: "column",
              }}
            >
              <NoPhotosIcon />
              <Typography sx={{ mt: "20px" }} variant="h3">
                No Photos
              </Typography>
              <Typography sx={{ textAlign: "center", mt: 1 }} variant="body2">
                {subTitle}
              </Typography>
              <MuiButton
                sx={{ mt: 5 }}
                aria-label="upload picture"
                component="label"
                variant="text"
              >
                <input
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={handleChange}
                  multiple
                />
                <Typography
                  variant="title2"
                  sx={{
                    color: theme.brand1[500],
                    textTransform: "none",
                  }}
                >
                  Upload Now
                </Typography>
              </MuiButton>
            </Box>
          )}
        </Box>
      ) : (
        <ImageList
          sx={{
            "& li": {
              borderRadius: "6px",
              overflow: "hidden",
            },
          }}
          gap={isMdSize ? 16 : 8}
          cols={isMdSize ? 5 : 3}
        >
          {!readOnly && numberOfImages < maxImage && (
            <ImageListItem
              sx={{
                height: "100%",
                width: "100%",
                backgroundColor: theme.whiteBg.WBG3,
              }}
            >
              <MuiButton
                color="primary"
                aria-label="upload picture"
                component="label"
                sx={{
                  height: "100%",
                  width: "100%",
                }}
              >
                <input
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={handleChange}
                  multiple
                />
                <BigAddIcon height={50} width={50} />
              </MuiButton>
            </ImageListItem>
          )}
          {imageData.map((item, index) => {
            return (
              <ImageListItem
                sx={{
                  aspectRatio: "1/1",
                  cursor: "pointer",
                  "& img": {
                    height: "100% !important",
                  },
                }}
                key={index}
                onClick={() => {
                  handlePhotoClick(index);
                }}
              >
                <Image
                  src={item?.url}
                  style={{ flexGrow: 1, objectFit: "cover" }}
                />
              </ImageListItem>
            );
          })}
        </ImageList>
      )}
      <PhotoDetailsModal
        readOnly={readOnly}
        handleNextImage={() => {
          selectedImageIndex < imageData.length - 1 &&
            setSelectedImageIndex((latestIndex) => latestIndex + 1);
        }}
        handlePreviousImage={() => {
          selectedImageIndex > 0 &&
            setSelectedImageIndex((latestIndex) => latestIndex - 1);
        }}
        handleClose={() => {
          setOpenModal(false);
        }}
        imgUrl={
          imageData[selectedImageIndex] && imageData[selectedImageIndex].url
        }
        handleImageRemove={handleImageRemove}
        open={openModal}
      />
    </Box>
  );
};

export default ImagesGrid;
